import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Snowflake, Plus, CalendarCheck, Repeat, Trash2, CreditCard, MapPin, Building2, Sparkles, Clock } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { PhotoUpload } from "@/components/PhotoUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { SlotPicker } from "@/components/SlotPicker";
import api, { apiError, API } from "@/lib/api";

const TYPES = [
  { key: "murale", label: "Murale (60€)" },
  { key: "console", label: "Console (70€)" },
  { key: "cassette", label: "Cassette (90€)" },
  { key: "gainable", label: "Gainable (110€)" },
];
const PERFUMES = ["Fraîcheur Marine", "Eucalyptus", "Lavande", "Agrumes", "Coton Frais", "Sans parfum"];
const STATUS = { available: "Disponible", claimed: "Acceptée", in_progress: "En cours", completed: "Terminée", cancelled: "Annulée" };
const PAY = { unpaid: { l: "À payer", c: "bg-amber-100 text-amber-700" }, paid: { l: "Payé", c: "bg-emerald-100 text-emerald-700" } };

export default function ClientDashboard() {
  const [tab, setTab] = useState("installations");
  const qc = useQueryClient();
  const { data: installs = [] } = useQuery({ queryKey: ["installations"], queryFn: async () => (await api.get("/installations")).data });
  const { data: bookings = [] } = useQuery({ queryKey: ["bookings"], queryFn: async () => (await api.get("/bookings/mine")).data });
  const { data: subs = [] } = useQuery({ queryKey: ["subs"], queryFn: async () => (await api.get("/subscriptions/mine")).data });

  const nav = [
    { key: "installations", label: "Mes clims", icon: Snowflake, onClick: () => setTab("installations") },
    { key: "reservations", label: "RDV", icon: CalendarCheck, onClick: () => setTab("reservations") },
    { key: "abonnements", label: "Contrats", icon: Repeat, onClick: () => setTab("abonnements") },
  ];

  const pay = async (missionId) => {
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, kind: "mission", reference_id: missionId });
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    const pending = localStorage.getItem("cl_pending_booking");
    const pendingSub = localStorage.getItem("cl_pending_subscription");
    if (pending) {
      localStorage.removeItem("cl_pending_booking");
      (async () => {
        try {
          await api.post("/bookings", JSON.parse(pending));
          toast.success("Votre réservation a été créée !");
          qc.invalidateQueries({ queryKey: ["bookings"] });
          setTab("reservations");
        } catch (e) { toast.error(apiError(e)); }
      })();
    }
    if (pendingSub) {
      localStorage.removeItem("cl_pending_subscription");
      (async () => {
        try {
          await api.post("/subscriptions", JSON.parse(pendingSub));
          toast.success("Votre forfait d'entretien est activé !");
          qc.invalidateQueries({ queryKey: ["subs"] });
          setTab("abonnements");
        } catch (e) { toast.error(apiError(e)); }
      })();
    }
  }, [qc]);

  return (
    <DashboardShell title="Espace Client" nav={nav} active={tab}>
      <UpsellCard bookings={bookings} subs={subs} goSub={() => setTab("abonnements")} />
      {tab === "installations" && <Installations installs={installs} qc={qc} />}
      {tab === "reservations" && <Reservations installs={installs} bookings={bookings} qc={qc} pay={pay} />}
      {tab === "abonnements" && <Abonnements subs={subs} qc={qc} />}
    </DashboardShell>
  );
}

function UpsellCard({ bookings, subs, goSub }) {
  const hasActive = (subs || []).some((s) => s.status === "active");
  if (hasActive || !bookings || bookings.length === 0) return null;
  const last = bookings[0];
  const nb = last?.nb_clim || 1;
  const unit = { murale: 60, console: 70, cassette: 90, gainable: 110 }[last?.service_type] || 60;
  const disc = nb >= 6 ? 0.15 : nb >= 3 ? 0.10 : 0;
  const monthly = Math.round(20 * nb * (1 - disc) * 100) / 100;
  const save = Math.max(0, Math.round(unit * nb * 4 - monthly * 12));
  return (
    <div data-testid="upsell-card" className="cl-gradient mb-6 flex flex-col items-start justify-between gap-4 rounded-2xl p-6 text-white sm:flex-row sm:items-center">
      <div>
        <h3 className="flex items-center gap-2 text-lg font-bold"><Repeat className="h-5 w-5" />Passez à l'abonnement entretien</h3>
        <p className="mt-1 max-w-xl text-sm text-sky-100">
          {save > 0
            ? `Visite automatique tous les 3 mois et tarif dégressif : économisez jusqu'à ${save}€/an par rapport aux interventions ponctuelles.`
            : "Visite automatique tous les 3 mois : votre clim reste saine sans y penser, à partir de "}{save > 0 ? "" : `${monthly}€/mois.`}
        </p>
      </div>
      <Button data-testid="upsell-cta" onClick={goSub} className="shrink-0 rounded-full bg-white text-[#263871] hover:bg-sky-50">
        Découvrir l'abonnement ({monthly}€/mois)
      </Button>
    </div>
  );
}

function Installations({ installs, qc }) {
  const [open, setOpen] = useState(false);
  const empty = { name: "", type: "murale", brand: "", power: "", accessibility: "", is_company: false, company_name: "", address: "", photo_path: "" };
  const [form, setForm] = useState(empty);
  const [photos, setPhotos] = useState([]);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    try {
      await api.post("/installations", { ...form, photo_path: photos[0] || null });
      toast.success("Installation ajoutée");
      setForm(empty); setPhotos([]); setOpen(false);
      qc.invalidateQueries({ queryKey: ["installations"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { await api.delete(`/installations/${id}`); qc.invalidateQueries({ queryKey: ["installations"] }); };

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#263871]">Mes installations</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-installation-btn" className="rounded-full bg-[#1D9BF0] hover:bg-[#57A4FF]"><Plus className="mr-1 h-4 w-4" />Ajouter</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nouvelle installation</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nom (ex: Salon)</Label><Input data-testid="inst-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" /></div>
              <div><Label>Type d'équipement</Label>
                <Select value={form.type} onValueChange={(v) => set("type", v)}><SelectTrigger data-testid="inst-type" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Marque</Label><Input data-testid="inst-brand" value={form.brand} onChange={(e) => set("brand", e.target.value)} className="mt-1" /></div>
                <div><Label>Puissance (option)</Label><Input data-testid="inst-power" value={form.power} onChange={(e) => set("power", e.target.value)} placeholder="ex: 3,5 kW" className="mt-1" /></div>
              </div>
              <div><Label>Adresse</Label><Input data-testid="inst-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1" /></div>
              <div><Label>Accessibilité</Label><Textarea data-testid="inst-access" value={form.accessibility} onChange={(e) => set("accessibility", e.target.value)} placeholder="Étage, échelle nécessaire, accès toiture…" className="mt-1" /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="inst-company" checked={form.is_company} onChange={(e) => set("is_company", e.target.checked)} />C'est pour une entreprise</label>
              {form.is_company && <Input data-testid="inst-company-name" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Nom de l'entreprise" />}
              <PhotoUpload value={photos} onChange={setPhotos} label="Photo de l'installation" testid="inst-photo" multiple={false} />
              <Button data-testid="save-installation" onClick={save} className="w-full rounded-full bg-[#263871]">Enregistrer</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {installs.length === 0 ? (
        <Empty text="Ajoutez votre première climatisation pour réserver une intervention." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {installs.map((i) => (
            <div key={i.id} data-testid={`installation-${i.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
              {i.photo_path && <img src={`${API}/files/${i.photo_path}`} alt="" className="mb-3 h-32 w-full rounded-xl object-cover" />}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-[#263871]">{i.name}</h3>
                  <p className="text-xs text-slate-500">{TYPES.find((t) => t.key === i.type)?.label} · {i.brand || "—"}</p>
                </div>
                <button onClick={() => del(i.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
              <p className="mt-2 flex items-center gap-1 text-xs text-slate-500"><MapPin className="h-3.5 w-3.5" />{i.address}</p>
              {i.is_company && <Badge className="mt-2 bg-[#E0F2FE] text-[#263871]"><Building2 className="mr-1 h-3 w-3" />{i.company_name || "Entreprise"}</Badge>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Reservations({ installs, bookings, qc, pay }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ address: "", nb_clim: 1, service_type: "murale", scheduled_date: "", slot: "matin", perfume: "Fraîcheur Marine", notes: "" });
  const set = (k, v) => setForm({ ...form, [k]: v });
  const price = { murale: 60, console: 70, cassette: 90, gainable: 110 }[form.service_type] * form.nb_clim;
  const minutes = 45 * form.nb_clim;
  const durText = minutes >= 60 ? `${Math.floor(minutes / 60)}h${minutes % 60 ? minutes % 60 : ""}` : `${minutes}min`;

  const book = async () => {
    if (!form.address || !form.scheduled_date) return toast.error("Adresse et date requises");
    try {
      await api.post("/bookings", { ...form, nb_clim: Number(form.nb_clim), installation_ids: [] });
      toast.success("Réservation créée ! Un technicien va l'accepter.");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["bookings"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#263871]">Mes réservations</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="book-btn" className="rounded-full bg-[#1D9BF0] hover:bg-[#57A4FF]"><Plus className="mr-1 h-4 w-4" />Réserver</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Réserver une intervention</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {installs.length > 0 && (
                <Select onValueChange={(v) => { const i = installs.find((x) => x.id === v); if (i) setForm({ ...form, address: i.address, service_type: i.type }); }}>
                  <SelectTrigger data-testid="book-install-select"><SelectValue placeholder="Pré-remplir depuis une installation" /></SelectTrigger>
                  <SelectContent>{installs.map((i) => <SelectItem key={i.id} value={i.id}>{i.name} — {i.address}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <div><Label>Adresse</Label>
                <div className="mt-1"><AddressAutocomplete testid="book-address" value={form.address} onSelect={({ label, lat, lon }) => setForm({ ...form, address: label, address_lat: lat, address_lon: lon })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Type</Label>
                  <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}><SelectTrigger data-testid="book-type" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label>Nb de clims</Label><Input data-testid="book-nb" type="number" min={1} value={form.nb_clim} onChange={(e) => set("nb_clim", e.target.value)} className="mt-1" /></div>
              </div>
              <div><Label>Date</Label><Input data-testid="book-date" type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value, scheduled_time: "" })} className="mt-1" /></div>
              <SlotPicker date={form.scheduled_date} nbClim={Number(form.nb_clim)} value={form.scheduled_time} onChange={(t) => set("scheduled_time", t)} testid="book-slots" />
              <Textarea data-testid="book-notes" placeholder="Notes (optionnel)" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
              <div><Label className="flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-[#1D9BF0]" />Parfum de finition</Label>
                <Select value={form.perfume} onValueChange={(v) => set("perfume", v)}><SelectTrigger data-testid="book-perfume" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{PERFUMES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-[#E0F2FE] p-3">
                <span className="flex items-center gap-1.5 text-sm text-slate-600"><Clock className="h-4 w-4 text-[#1D9BF0]" />Durée ~ {durText}</span>
                <span className="text-xl font-bold text-[#263871]">{price}€ HT</span>
              </div>
              <Button data-testid="confirm-book" onClick={book} className="w-full rounded-full bg-[#263871]">Confirmer la réservation</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {bookings.length === 0 ? <Empty text="Aucune réservation pour le moment." /> : (
        <div className="space-y-3">
          {bookings.map((b) => (
            <div key={b.id} data-testid={`booking-${b.id}`} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-[#263871]">{b.nb_clim} clim · {b.service_type}</h3>
                  <Badge variant="outline">{STATUS[b.status]}</Badge>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${PAY[b.payment_status]?.c}`}>{PAY[b.payment_status]?.l}</span>
                </div>
                <p className="mt-1 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" />{b.address}</p>
                <p className="text-xs text-slate-400">{b.scheduled_date} · {b.slot} · {b.price_total}€ HT{b.duration_minutes ? ` · ~${b.duration_minutes}min` : ""}{b.perfume ? ` · ${b.perfume}` : ""}</p>
                {b.technician_name && <p className="text-xs text-slate-500">Technicien : {b.technician_name}</p>}
              </div>
              {b.payment_status === "unpaid" && (
                <Button data-testid={`pay-${b.id}`} onClick={() => pay(b.id)} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><CreditCard className="mr-1 h-4 w-4" />Payer {b.price_total}€</Button>
              )}
              {(b.photos_before?.length > 0 || b.photos_after?.length > 0) && (
                <div className="flex gap-2">
                  {b.photos_before?.map((p) => <img key={p} src={`${API}/files/${p}`} className="h-12 w-12 rounded-lg object-cover ring-2 ring-amber-300" alt="avant" />)}
                  {b.photos_after?.map((p) => <img key={p} src={`${API}/files/${p}`} className="h-12 w-12 rounded-lg object-cover ring-2 ring-emerald-400" alt="après" />)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Abonnements({ subs, qc }) {
  const [form, setForm] = useState({ nb_clim: 1, service_type: "murale" });
  const set = (k, v) => setForm({ ...form, [k]: v });
  const nb = Number(form.nb_clim) || 1;
  const discount = nb >= 6 ? 0.15 : nb >= 3 ? 0.10 : 0;
  const monthly = Math.round(20 * nb * (1 - discount) * 100) / 100;
  const subscribe = async () => {
    try {
      await api.post("/subscriptions", { ...form, nb_clim: nb });
      toast.success("Forfait d'entretien souscrit !");
      qc.invalidateQueries({ queryKey: ["subs"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div>
      <h2 className="mb-1 text-lg font-bold text-[#263871]">Forfaits d'entretien</h2>
      <p className="mb-5 text-sm text-slate-500">On passe tous les 3 mois. 20€/mois/clim, dégressif : −10% dès 3 clims, −15% dès 6 clims.</p>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="cl-gradient rounded-2xl p-6 text-white lg:col-span-1">
          <h3 className="text-lg font-bold">Souscrire un forfait trimestriel</h3>
          <div className="mt-4 space-y-3">
            <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}><SelectTrigger data-testid="sub-type" className="bg-white text-slate-800"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent></Select>
            <Input data-testid="sub-nb" type="number" min={1} value={form.nb_clim} onChange={(e) => set("nb_clim", e.target.value)} className="bg-white text-slate-800" placeholder="Nb de clims" />
            <div className="rounded-xl bg-white/15 p-3 text-center">
              <p className="text-2xl font-extrabold">{monthly}€<span className="text-sm font-medium text-sky-200">/mois</span></p>
              {discount > 0 && <p className="text-xs text-sky-200">Remise {Math.round(discount * 100)}% appliquée</p>}
            </div>
            <Button data-testid="subscribe-btn" onClick={subscribe} className="w-full rounded-full bg-white text-[#263871] hover:bg-sky-50">Souscrire</Button>
          </div>
        </div>
        <div className="lg:col-span-2">
          {subs.length === 0 ? <Empty text="Aucun forfait actif." /> : (
            <div className="space-y-3">
              {subs.map((s) => (
                <div key={s.id} data-testid={`sub-${s.id}`} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-5">
                  <div>
                    <h3 className="font-bold text-[#263871]">Forfait {s.frequency}</h3>
                    <p className="text-xs text-slate-500">{s.nb_clim} clim · {s.service_type} · Prochaine visite : {s.next_visit}{s.discount ? ` · −${Math.round(s.discount * 100)}%` : ""}</p>
                  </div>
                  <div className="text-right"><Badge className="bg-emerald-100 text-emerald-700">{s.status}</Badge><p className="mt-1 font-bold text-[#263871]">{s.monthly_price || s.price}€/mois</p></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-500">{text}</div>;
}
